import React, { useCallback, useEffect, useRef, useState } from 'react';
import './FavouriteStations.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroller';
import Input from '../../../Components/Common/Input/Input';
import FilterBtn from '../../../Components/Common/FilterBtn/FilterBtn';
import MiniStationCard from '../../../Components/MiniStationCard/MiniStationCard';
import { HeartSvg } from '../../../Components/Common/HeartToggle/HeartToggle';
import { Button } from '../../../Components/Common';
import ArrowLeft from '../../../Assets/icons/ArrowLeft';
import PreLoader from '../../../Assets/icons/PreLoader/PreLoader';
import { Pathname } from '../../../Router/Pathname';
import { fetchConnectorStandards } from '../SearchStationsView/redux/action';
import {
    getFavouriteStations,
    getFavouriteSummary,
    resolveIsFavourite,
    setFavouriteOverride,
} from './redux/favouriteStationSlice';
import {
    useFavouriteToggle,
    usePullToRefresh,
    useRefreshOnFocus,
} from '../../../Hooks';
import { getStationStatus, prettyConnectorStandard } from '../../../Utils';


export const FavouriteStationsCard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { summary, summaryLoading } = useSelector(
        (store) => store.favouriteStations
    );

    const loadSummary = useCallback(() => {
        dispatch(getFavouriteSummary());
    }, [dispatch]);

    useEffect(() => {
        loadSummary();
    }, [loadSummary]);

   
    useRefreshOnFocus(loadSummary);

    // First load only — a refresh keeps the numbers we already have on screen.
    const isSkeleton = summaryLoading && !summary;
    const hasFavourites = Boolean(summary?.has_favourites);
    const isEmpty = Boolean(summary) && !hasFavourites;

    const openFavourites = () => navigate(Pathname.FAVOURITE_STATIONS);

    const handleAddStation = (event) => {
        event.stopPropagation();
        navigate(Pathname.SEARCH_STATIONS);
    };

    return (
        <div
            className="FavouriteCard"
            onClick={openFavourites}
            role="button"
            tabIndex={0}
            onKeyPress={openFavourites}
        >
            <div className="FavouriteCard__Icon">
                <HeartSvg filled size={24} />
            </div>

            <div className="FavouriteCard__Content">
                <p className="FavouriteCard__Title">Favourite Stations</p>

                {isSkeleton && <span className="FavouriteCard__Skeleton" />}

                {/* If the summary never arrived the card still renders, just
                    without a subtitle, rather than breaking the dashboard. */}
                {!isSkeleton && hasFavourites && (
                    <p className="FavouriteCard__SubTitle">
                        {`${summary?.saved_count || 0} saved · ${
                            summary?.available_count || 0
                        } available now`}
                    </p>
                )}

                {/* A caption for the + rather than its own tap target, so the
                    card body has exactly one destination. */}
                {!isSkeleton && isEmpty && (
                    <p className="FavouriteCard__Link">Add station</p>
                )}
            </div>

            {/* Nothing saved yet: a + to add one. Otherwise: into the list. */}
            {isEmpty ? (
                <button
                    type="button"
                    className="FavouriteCard__Add"
                    onClick={handleAddStation}
                    aria-label="Add a favourite station"
                >
                    <svg width={16} height={16} viewBox="0 0 24 24" fill="none">
                        <path
                            d="M6 12h12M12 6v12"
                            stroke="#ffffff"
                            strokeWidth={2.2}
                            strokeLinecap="round"
                        />
                    </svg>
                </button>
            ) : (
                <svg width={20} height={20} viewBox="0 0 24 24" fill="none">
                    <path
                        d="m8.91 19.92 6.52-6.52a1.98 1.98 0 0 0 0-2.8L8.91 4.08"
                        stroke="#8c8c8c"
                        strokeWidth={1.8}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            )}
        </div>
    );
};

/** Screen C — the favourites list. */
const FavouriteStationsView = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { stations, loading, hasMore, page, error, favouriteOverrides } = useSelector(
        (store) => store.favouriteStations
    );
    const { connectorStandards } = useSelector((store) => store.stations);

    const [activeFilter, setActiveFilter] = useState(null);
    const [stationName, setStationName] = useState(null);
    const filterRowRef = useRef(null);
    const parkedFilterRow = useRef(false);

    useEffect(() => {
        const row = filterRowRef.current;
        const favouritesChip = row?.children?.[1];
        if (parkedFilterRow.current || !row || !favouritesChip) return;
        if (row.scrollWidth <= row.clientWidth) return;
        const offset = favouritesChip.offsetLeft - row.offsetLeft;
        if (!offset) return;
        row.scrollLeft = offset;
        parkedFilterRow.current = true;
    }, [connectorStandards]);

    // The row stays on screen with an empty heart; the next fetch is what
    // drops it. Nothing moves under the user, so no undo is needed.
    const applyLocalFavourite = useCallback(
        (stationId, isFavourite) => {
            dispatch(setFavouriteOverride({ id: stationId, is_favourite: isFavourite }));
        },
        [dispatch]
    );

    const { toggleFavourite } = useFavouriteToggle({ applyLocal: applyLocalFavourite });

    const loadFavourites = useCallback(
        (nextPage = 0) =>
            dispatch(
                getFavouriteStations({
                    station_name: stationName,
                    connector_type: activeFilter,
                    page: nextPage,
                })
            ),
        [dispatch, stationName, activeFilter]
    );

    // Live availability — reload on focus and on pull-to-refresh.
    const reloadFavourites = useCallback(() => loadFavourites(0), [loadFavourites]);
    useRefreshOnFocus(reloadFavourites);
    const { pullProps, pullIndicator } = usePullToRefresh(reloadFavourites);

    useEffect(() => {
        const debouncedSearch = setTimeout(() => loadFavourites(0), 500);
        return () => clearTimeout(debouncedSearch);
    }, [stationName, activeFilter]);

    useEffect(() => {
        dispatch(fetchConnectorStandards());
    }, []);

    const loadMore = () => {
        // `count` on this endpoint is the total, so `hasMore` already knows
        // whether another page exists. `page > 0` keeps the scroller from
        // racing the initial page-0 fetch.
        if (!loading && hasMore && page > 0) loadFavourites(page);
    };

    const handleCardNavigation = (station) =>
        navigate(Pathname.SEARCH_CONNECTOR, {
            state: {
                id: station?.id,
                name: station?.station_name,
                address: station?.address,
                is_favourite: true,
                from: Pathname.FAVOURITE_STATIONS,
            },
        });

    const renderStationCards = () =>
        stations?.map((station) => {
            const isFavourite = resolveIsFavourite(favouriteOverrides, station);
            return (
                <MiniStationCard
                    key={station?.id}
                    name={station?.station_name}
                    address={`${station?.address}`}
                    status={getStationStatus(station)}
                    isFavourite={isFavourite}
                    onToggleFavourite={() => toggleFavourite(station?.id, isFavourite)}
                    onClick={() => handleCardNavigation(station)}
                />
            );
        });

    const renderEmptyState = () => (
        <div className="FavouriteStations__Empty">
            <p className="FavouriteStations__EmptyTitle">
                {stationName || activeFilter
                    ? 'No favourites match this search'
                    : 'No favourite stations yet'}
            </p>
            <p className="FavouriteStations__EmptyText">
                Tap the heart on any station to keep it here for next time.
            </p>
            <Button
                text="Search Stations"
                onClick={() => navigate(Pathname.SEARCH_STATIONS)}
            />
        </div>
    );

    return (
        <main className="FavouriteStations" {...pullProps}>
            <section className="FavouriteStations__Header">
                <div
                    className="FavouriteStations__Left"
                    onClick={() => navigate(Pathname.HOME)}
                >
                    <ArrowLeft />
                </div>
                <div className="FavouriteStations__Title">Favourite Stations</div>
            </section>

            <div className="FavouriteStations__Input">
                <Input
                    label={'Favourite Stations'}
                    value={stationName}
                    onValueChange={setStationName}
                />
            </div>
            <div className="FavouriteStations__FilterContainer" ref={filterRowRef}>
                {/* "All" means every station, so it leaves for the search
                    screen. It starts scrolled out of view on the left. */}
                <FilterBtn
                    text={'All'}
                    handleClick={() => navigate(Pathname.SEARCH_STATIONS)}
                    activeStatus={false}
                />
                {/* This screen is already favourites-only, so "Favourites"
                    plays the part "All" plays on the search screen: it is the
                    no-connector-filter option, highlighted on arrival. */}
                <FilterBtn
                    text={'Favourites'}
                    handleClick={() => setActiveFilter(null)}
                    activeStatus={activeFilter === null}
                />
                {connectorStandards?.map((item) => (
                    <FilterBtn
                        // The standard itself is the stable identity; an index
                        // would re-key every chip if the enum order changed.
                        key={item}
                        text={prettyConnectorStandard(item)}
                        id={item}
                        handleClick={() => setActiveFilter(item)}
                        activeStatus={activeFilter === item}
                    />
                ))}
            </div>

            <div>
                {pullIndicator}
                <InfiniteScroll
                    pageStart={0}
                    loadMore={loadMore}
                    hasMore={hasMore}
                    loader={
                        <div className="FavouriteStations__Loader" key={0}>
                            <PreLoader width="48px" height="48px" />
                        </div>
                    }
                >
                    <div className="FavouriteStations__Cards">
                        {stations?.length > 0
                            ? renderStationCards()
                            : loading
                            ? null
                            : renderEmptyState()}
                    </div>
                </InfiniteScroll>
            </div>

            {error && !stations?.length && (
                <p className="FavouriteStations__Error">{error}</p>
            )}
        </main>
    );
};

export default FavouriteStationsView;
